import React, { useEffect, useState } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import {
  Briefcase,
  ChartNoAxesCombined,
  BadgeCheck,
  Award,
  Hash,
  Copy,
  IndianRupee,
  Calendar,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import Section from 'src/components/ui/section';
import { usePersonaMasterData } from 'src/services/api/queries/personaQueries';

const RequirementReadOnlyView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [requirementData, setRequirementData] = useState(null);
  const [masterData, setMasterData] = useState({
    skillOptions: [],
    certificationOptions: [],
    fieldOfStudyOptions: [],
    workExperienceOptions: [],
    customerTypeOptions: [],
    careerLevelOptions: [],
  });

  const { data: masterDataResponse } = usePersonaMasterData();

  useEffect(() => {
    // First check sessionStorage for newly created requirements
    const storedData = sessionStorage.getItem('pendingRequirement');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        // Only use sessionStorage data if it matches the current ID
        if (data.id?.toString() === id) {
          setRequirementData(data);
          return;
        }
      } catch (error) {
        console.error('Error parsing requirement data:', error);
      }
    }

    // TODO: Replace with actual API call to fetch requirement by ID
    // When API is ready, use something like:
    // const { data: requirementResponse, isLoading } = useRequirementInfo(id);
    // if (requirementResponse?.data) {
    //   setRequirementData(requirementResponse.data);
    // }

    // For now, if not in sessionStorage, we'll need to fetch from API
    // This is a placeholder - replace with actual API call
    // For existing requirements that are already saved, fetch from backend
    const fetchRequirement = async () => {
      try {
        // TODO: Implement API call
        // const response = await RequirementService.getRequirementById(id);
        // setRequirementData(response.data);

        // Temporary: Show error if data not found
        // Remove this once API is implemented
        toast({
          title: 'Error',
          description:
            'Requirement not found. Please ensure the requirement exists.',
          variant: 'destructive',
        });
        // Don't redirect immediately - allow user to go back manually
        // navigate('/source-candidates');
      } catch (error) {
        console.error('Error fetching requirement:', error);
        toast({
          title: 'Error',
          description: 'Failed to load requirement data',
          variant: 'destructive',
        });
      }
    };

    // Only fetch if not found in sessionStorage
    if (!storedData) {
      fetchRequirement();
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    if (masterDataResponse?.data) {
      const responseData = masterDataResponse.data;
      setMasterData({
        skillOptions:
          responseData?.skills?.map((item) => ({
            value: item.id.toString(),
            label: item.name,
          })) || [],
        certificationOptions:
          responseData?.certifications?.map((item) => ({
            value: item.id.toString(),
            label: item.name,
          })) || [],
        fieldOfStudyOptions:
          responseData?.field_of_study?.map((item) => ({
            value: item.id.toString(),
            label: item.name,
          })) || [],
        workExperienceOptions:
          responseData?.work_experience?.map((item) => ({
            value: item.id.toString(),
            label: item.value,
          })) || [],
        customerTypeOptions:
          responseData?.customer_tier?.map((item) => ({
            value: item.id.toString(),
            label: item.value,
          })) || [],
        careerLevelOptions:
          responseData?.career_level?.map((item) => ({
            value: item.id.toString(),
            label: item.value,
          })) || [],
      });
    }
  }, [masterDataResponse]);

  if (!requirementData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading requirement data...</p>
      </div>
    );
  }

  const getSkillName = (skillId) => {
    const skill = masterData.skillOptions.find((s) => s.value === skillId);
    return skill?.label || skillId;
  };

  const getCertificationName = (certId) => {
    const cert = masterData.certificationOptions.find(
      (c) => c.value === certId
    );
    return cert?.label || certId;
  };

  const getFieldOfStudyName = (fieldId) => {
    const field = masterData.fieldOfStudyOptions.find(
      (f) => f.value === fieldId
    );
    return field?.label || fieldId;
  };

  const getWorkExperienceLabel = (expId) => {
    const exp = masterData.workExperienceOptions.find((e) => e.value === expId);
    return exp?.label || expId;
  };

  const getCareerLevelLabel = (levelId) => {
    const level = masterData.careerLevelOptions.find(
      (l) => l.value === levelId
    );
    return level?.label || levelId;
  };

  const formatCurrency = (val) => {
    if (!val) return '0';
    const num = parseInt(String(val).replace(/[^0-9]/g, ''), 10);
    return (num / 100000).toFixed(2);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(requirementData.registration_id);
      toast({
        title: 'Copied',
        description: 'Requirement ID copied to clipboard',
        duration: 1500,
      });
    } catch (err) {
      toast({
        title: 'Copy failed',
        variant: 'destructive',
        duration: 1500,
      });
    }
  };

  const handleAssignPartners = () => {
    navigate(`/source-candidates/${requirementData.id}/assign-partners`);
  };

  const handleViewSubmissions = () => {
    navigate(`/source-candidates/${requirementData.id}`);
  };

  return (
    <>
      <PageHeader
        title={requirementData.title}
        actions={
          <>
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={() => navigate('/source-candidates')}
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.8} />
              Back
            </Button>
            <Button
              variant="outline"
              size="headerSm"
              className="gap-1.5"
              onClick={handleAssignPartners}
            >
              Assign Partners
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
            </Button>
          </>
        }
      />

      <div className="text-left space-y-4 p-6 bg-muted">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-foreground-strong font-semibold md:text-md pageTitle relative flex items-center gap-2">
            Requirement Summary
          </h3>
          <Button
            size="sm"
            variant="default"
            className="flex items-center gap-2"
            onClick={handleViewSubmissions}
          >
            View Submissions
          </Button>
        </div>
        <div className="space-y-6">
          <div className="grid grid-cols-[3fr_1fr] gap-8">
            <div className="space-y-4">
              <Section title="About the Role">
                <div className="grid grid-cols-3 gap-4 border-b border-gray-200 pb-4">
                  <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <div className="p-2 bg-brand-bg-subtle rounded-md">
                      <Briefcase className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-foreground-strong flex flex-col">
                      <span className="text-gray-500 text-xs font-normal">
                        Career Level
                      </span>
                      {getCareerLevelLabel(requirementData.career_level)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                    <div className="p-2 bg-brand-bg-subtle rounded-md">
                      <ChartNoAxesCombined className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-semibold text-foreground-strong flex flex-col">
                      <span className="text-gray-500 text-xs font-normal">
                        Work Experience
                      </span>
                      {getWorkExperienceLabel(
                        requirementData.work_experience_id
                      )}
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <span className="text-foreground-strong text-xs font-semibold">
                    Persona Description
                  </span>
                  <span className="text-sm text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
                    {requirementData.persona_description}
                  </span>
                </div>
                <div className="mt-4">
                  <span className="text-foreground-strong text-xs font-semibold">
                    Detailed Job Description
                  </span>
                  <span className="text-sm text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
                    {requirementData.detailed_job_description}
                  </span>
                </div>
              </Section>
              <Section title="Eligibility">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h6 className="flex items-center gap-2 text-sm font-semibold text-foreground-strong">
                      Education
                    </h6>
                  </div>
                  <div className="overflow-hidden border border-border rounded-xl">
                    <table className="w-full">
                      <thead>
                        <tr>
                          <th className="text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground py-[13px] px-4 bg-card whitespace-nowrap border-b border-border">
                            Level
                          </th>
                          <th className="text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground py-[13px] px-4 bg-card whitespace-nowrap border-b border-border">
                            Field of Study
                          </th>
                          <th className="text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground py-[13px] px-4 bg-card whitespace-nowrap border-b border-border">
                            Requirement
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {requirementData.fieldOfStudy?.map((field) => (
                          <tr
                            key={`field-${field.value}`}
                            className="hover:bg-muted transition-colors"
                          >
                            <td className="px-4 py-3 border-b border-gray-100">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-700">
                                  {requirementData.education_level === 'DEGREE'
                                    ? 'Degree'
                                    : 'Masters'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              <span className="text-sm font-medium text-foreground-strong">
                                {getFieldOfStudyName(field.value)}
                              </span>
                            </td>
                            <td className="px-4 py-3 border-b border-gray-100">
                              {field.isMandatory ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-blue-500/100 border border-blue-500/50">
                                  Required
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-emerald-500/100 border border-emerald-500/50">
                                  Optional
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h6 className="flex items-center gap-2 text-sm font-semibold text-foreground-strong">
                      <BadgeCheck className="h-4 w-4 text-blue-700" />
                      Skills
                    </h6>
                  </div>
                  <div className="border border-gray-200 rounded-xl p-4">
                    <div className="flex pb-2">
                      <div className="text-xs font-semibold text-gray-500 w-[50%]">
                        REQUIRED
                        <div className="flex flex-wrap gap-2">
                          {requirementData.skills
                            ?.filter((skill) => skill.isMandatory)
                            .map((skill) => (
                              <div
                                key={`skill-mandatory-${skill.value}`}
                                className="font-normal text-xs border border-blue-500 rounded-xl px-2 py-1 text-blue-500 w-fit mt-2"
                              >
                                {getSkillName(skill.value)}
                              </div>
                            ))}
                          {(!requirementData.skills ||
                            requirementData.skills.filter((s) => s.isMandatory)
                              .length === 0) && (
                            <div className="font-normal text-xs border border-gray-500 rounded-xl px-2 py-1 text-gray-500 w-fit mt-2">
                              None
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs font-semibold text-gray-500 w-[50%]">
                        NICE TO HAVE
                        <div className="flex flex-wrap gap-2">
                          {requirementData.skills
                            ?.filter((skill) => !skill.isMandatory)
                            .map((skill) => (
                              <div
                                key={`skill-optional-${skill.value}`}
                                className="font-normal text-xs border border-emerald-500/50 rounded-xl px-2 py-1 text-emerald-500/100 w-fit mt-2"
                              >
                                {getSkillName(skill.value)}
                              </div>
                            ))}
                          {(!requirementData.skills ||
                            requirementData.skills.filter((s) => !s.isMandatory)
                              .length === 0) && (
                            <div className="font-normal text-xs border border-gray-500 rounded-xl px-2 py-1 text-gray-500 w-fit mt-2">
                              None
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h6 className="flex items-center gap-2 text-sm font-semibold text-foreground-strong">
                      <Award className="h-4 w-4 text-blue-700" />
                      Certifications
                    </h6>
                  </div>
                  <div className="border border-gray-200 rounded-xl p-4">
                    <div className="flex pb-2">
                      <div className="text-xs font-semibold text-gray-500 w-[50%]">
                        REQUIRED
                        <div className="flex flex-wrap gap-2">
                          {requirementData.certifications
                            ?.filter((cert) => cert.isMandatory)
                            .map((cert) => (
                              <div
                                key={`cert-mandatory-${cert.value}`}
                                className="font-normal text-xs border border-blue-500 rounded-xl px-2 py-1 text-blue-500 w-fit mt-2"
                              >
                                {getCertificationName(cert.value)}
                              </div>
                            ))}
                          {(!requirementData.certifications ||
                            requirementData.certifications.filter(
                              (c) => c.isMandatory
                            ).length === 0) && (
                            <div className="font-normal text-xs border border-gray-500 rounded-xl px-2 py-1 text-gray-500 w-fit mt-2">
                              None
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs font-semibold text-gray-500 w-[50%]">
                        NICE TO HAVE
                        <div className="flex flex-wrap gap-2">
                          {requirementData.certifications
                            ?.filter((cert) => !cert.isMandatory)
                            .map((cert) => (
                              <div
                                key={`cert-optional-${cert.value}`}
                                className="font-normal text-xs border border-emerald-500/50 rounded-xl px-2 py-1 text-emerald-500/100 w-fit mt-2"
                              >
                                {getCertificationName(cert.value)}
                              </div>
                            ))}
                          {(!requirementData.certifications ||
                            requirementData.certifications.filter(
                              (c) => !c.isMandatory
                            ).length === 0) && (
                            <div className="font-normal text-xs border border-gray-500 rounded-xl px-2 py-1 text-gray-500 w-fit mt-2">
                              None
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Section>
            </div>
            <div className="space-y-4">
              <Section title="At a glance">
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center gap-2 justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-500 mb-1 text-xs flex items-center gap-2">
                      <span className="p-2 bg-brand-bg-subtle rounded-md">
                        <Hash className="h-4 w-4 text-primary" />
                      </span>
                      Req ID
                    </span>
                    <span className="text-xs font-semibold text-foreground-strong flex items-center gap-2 group">
                      <button
                        type="button"
                        aria-label="Copy requirement ID"
                        onClick={handleCopy}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary p-1 rounded"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      {requirementData.registration_id}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-500 mb-1 text-xs flex items-center gap-2">
                      <span className="p-2 bg-brand-bg-subtle rounded-md">
                        <IndianRupee className="h-4 w-4 text-primary" />
                      </span>
                      Salary Range (LPA)
                    </span>
                    <span className="text-xs text-foreground-strong font-semibold flex items-center gap-2">
                      {formatCurrency(requirementData.minimum_salary)} -{' '}
                      {formatCurrency(requirementData.maximum_salary)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 justify-between border-b border-gray-200 pb-1">
                    <span className="text-gray-500 mb-1 text-xs flex items-center gap-2">
                      <span className="p-2 bg-brand-bg-subtle rounded-md">
                        <Calendar className="h-4 w-4 text-primary" />
                      </span>
                      Created
                    </span>
                    <span className="text-xs text-foreground-strong font-semibold mb-2 flex items-center gap-2">
                      {new Date(requirementData.created_at).toLocaleDateString(
                        'en-GB',
                        {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        }
                      )}
                    </span>
                  </div>
                </div>
              </Section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RequirementReadOnlyView;
